import type React from 'react';
import ReactECharts from 'echarts-for-react';
import {Typography} from '@mui/material';
import type {SpendAnalysis} from '../../types/categoryProfileTypes';
import {LOCALE} from '~/pages/category-profile/domain/locale';

type Props = {volume: SpendAnalysis['volume']};

const MONTH = new Intl.DateTimeFormat(LOCALE, {month: 'short', timeZone: 'UTC'});
const NAVY = '#0B1F3A';
const qty = (v: number) => v.toLocaleString(LOCALE, {maximumFractionDigits: v < 10 ? 2 : 0});

/** Volume: actual this year (solid) and forecast for the remaining months (dashed outline), in the base unit. */
export const VolumeChart: React.FC<Props> = ({volume}) => {
    if (!volume || volume.points.every(p => !p.actual && !p.forecast)) {
        return (
            <Typography variant="body2" color="text.secondary" sx={{py: 6, textAlign: 'center'}}>
                No volume yet: it comes from items with an internal benchmark (a quantity and a unit).
            </Typography>
        );
    }
    const months = volume.points.map(p => MONTH.format(new Date(`${p.month}T00:00:00Z`)));
    return (
        <ReactECharts style={{height: 240}} notMerge option={{
            grid: {left: 56, right: 12, top: 28, bottom: 24},
            legend: {top: 0, right: 0, data: ['Actual', 'Forecast']},
            tooltip: {trigger: 'axis', axisPointer: {type: 'shadow'},
                valueFormatter: (v: number | null) => (v === null || v === undefined ? '—' : `${qty(v)} ${volume.unit}`)},
            xAxis: {type: 'category', data: months, axisTick: {show: false}},
            yAxis: {type: 'value', name: volume.unit, splitNumber: 3, axisLabel: {formatter: (v: number) => qty(v)}},
            series: [
                {name: 'Actual', type: 'bar', stack: 'v', barWidth: '55%', data: volume.points.map(p => p.actual),
                    itemStyle: {color: NAVY, borderRadius: [2, 2, 0, 0]}},
                {name: 'Forecast', type: 'bar', stack: 'v', barWidth: '55%', data: volume.points.map(p => p.forecast),
                    itemStyle: {color: 'transparent', borderColor: NAVY, borderType: 'dashed', borderWidth: 1.5}},
            ],
        }}/>
    );
};
